import type { Camera } from '@babylonjs/core/Cameras/camera';
import { ImageProcessingConfiguration } from '@babylonjs/core/Materials/imageProcessingConfiguration';
import { ColorCurves } from '@babylonjs/core/Materials/colorCurves';
import { Color4 } from '@babylonjs/core/Maths/math.color';
import { ImageProcessingPostProcess } from '@babylonjs/core/PostProcesses/imageProcessingPostProcess';

import { renderFeatures } from './compat';

/**
 * The colour grade and the vignette — the screen pass.
 *
 * `architecture/AUDIO-VISUAL-EFFECTS.md` §5.5 is the contract. In one line: **a darker, punchier
 * frame with dark corners, drawn by one post-process that touches no material.**
 *
 * The numbers are **chosen by looking**, starting from `ideas/COOL-VISUAL-EFFECTS.md`: contrast
 * deepens the blacks the world is built around while the lit windows, portals and spells keep
 * their value; a small desaturation stops the darks going muddy-green in the crypts; the vignette
 * pulls the eye to the middle of the screen, which is where the character always is.
 */
export const GRADE = {
  contrast: 1.12,
  exposure: 1.0,
  /** `ColorCurves.globalSaturation` is −100..100; the idea's −10 %. */
  saturation: -10,
  /**
   * Babylon's vignette weight — its own curve, default 1.5, not the idea's 0–1. 1.4 darkens the
   * corners without reaching the HUD's bars, which sit on the screen's edges and must stay legible.
   */
  vignetteWeight: 1.4,
  vignetteStretch: 0.3,
} as const;

/** Grades whose shader failed and which disposed themselves — never to be re-attached. */
const broken = new WeakSet<ImageProcessingPostProcess>();

/** Whether a grade is still usable: `PostProcess` has no `isDisposed`, so the failure is recorded. */
export function gradeUsable(grade: object | null): boolean {
  return grade !== null && !broken.has(grade as ImageProcessingPostProcess);
}

/**
 * Builds the grade and attaches it to the camera. Returns it so the caller can switch it (attach
 * and detach — a post-process has no enabled flag) or `null` when it is switched off.
 *
 * **Its own `ImageProcessingConfiguration`, never the scene's.** The scene's feeds every material's
 * shader defines, and changing it recompiles every material in the world: a new define in every
 * material shader is the category of change that has blacked this world before. A configuration
 * of its own is read by this pass alone.
 */
export function attachColourGrade(camera: Camera): ImageProcessingPostProcess | null {
  if (!renderFeatures.grading) return null;

  const config = new ImageProcessingConfiguration();
  config.contrast = GRADE.contrast;
  config.exposure = GRADE.exposure;
  config.colorCurvesEnabled = true;
  const curves = new ColorCurves();
  curves.globalSaturation = GRADE.saturation;
  config.colorCurves = curves;
  config.vignetteEnabled = true;
  config.vignetteWeight = GRADE.vignetteWeight;
  config.vignetteStretch = GRADE.vignetteStretch;
  config.vignetteColor = new Color4(0, 0, 0, 0);
  config.vignetteBlendMode = ImageProcessingConfiguration.VIGNETTEMODE_MULTIPLY;
  // No tone mapping: the scene is LDR and already authored dark; a tone map would lift the floor.
  config.toneMappingEnabled = false;

  const grade = new ImageProcessingPostProcess(
    'grind-colour-grade',
    1.0,
    camera,
    undefined,
    undefined,
    false,
    undefined,
    config,
  );
  // **Antialiasing back.** A post-process makes the scene render into a texture, and a texture has
  // none of the canvas's MSAA, so every roof edge would step. Four samples on this target restore
  // it — FXAA, the idea's suggestion, would smear the painted textures instead.
  grade.samples = 4;
  // **The scene is already in gamma space.** The pass assumes linear input by default and converts
  // it, which brightened the whole frame 2.5× in the first browser capture (centre 36.8 → 92.7 of
  // 255): a grade that was meant to deepen the blacks washed the town out to grey.
  grade.fromLinearSpace = false;

  // Detach rather than black out, the rule `attachPixelArt` follows: when a pass's shader fails,
  // the geometry still renders — into a target nothing copies out.
  grade.onEffectCreatedObservable.add((effect) => {
    effect.onError = (_e, errors) => {
      console.error('grind: colour-grade shader failed to compile, falling back to the raw scene\n', errors);
      broken.add(grade);
      grade.dispose(camera);
    };
  });

  return grade;
}
